import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { map, Observable } from 'rxjs';

import { point } from '@turf/helpers';
import distance from '@turf/distance';
import greatCircle from '@turf/great-circle';

import { RadarConfig } from '../configs/config';
import {
  PIN_ALPHABET,
  PIN_COLORS,
} from 'src/app/shared/constants/map-pin.constant';

export interface IRouteBTWD {
  value: number;
  text: string;
}

export interface IRouteBTWLeg {
  startLocation: {
    latitude: number;
    longitude: number;
  };
  endLocation: {
    latitude: number;
    longitude: number;
  };
  duration: IRouteBTWD;
  distance: IRouteBTWD;
  geometry: {
    polyline: string;
  };
}

export interface IRadarRoute {
  duration: IRouteBTWD;
  distance: IRouteBTWD;
  legs: IRouteBTWLeg[];
}

export interface IRadarDirectionsResp {
  meta: {
    code: 200 | 400;
  };
  routes: IRadarRoute[];
}

@Injectable({
  providedIn: 'root',
})
export class MapService {
  constructor(private http: HttpClient) {}

  get polylinePathOptions() {
    return {
      id: this.generateUniqueId(),
      precision: 6,
      paint: {
        'line-color': '#F58025',
      },
    };
  }

  getDirectionsRoutesAndLegs(points: string): Observable<IRouteBTWLeg[]> {
    return this.http
      .get<IRadarDirectionsResp>(
        `https://api.radar.io/v1/route/directions?locations=${points}&units=metric`,
        {
          headers: {
            Authorization: RadarConfig.radarLiveKey,
          },
        },
      )
      .pipe(
        map((res) => {
          const data = res.routes[0];
          return data.legs;
        }),
      );
  }

  createFitToZoomCtrlElement(): HTMLElement {
    const ctrlEl = document.createElement('button');
    ctrlEl.className = 'maplibregl-ctrl-geolocate';
    ctrlEl.innerHTML = '<span class="maplibregl-ctrl-icon"></span>';
    return ctrlEl;
  }

  createBookingPopupElement(link: string): HTMLElement {
    const bookingPopupEl = document.createElement('div');
    const linkEl = document.createElement('a');

    linkEl.href = link;
    linkEl.target = '_blank';
    linkEl.textContent = 'Go to booking';
    linkEl.className = 'map-b-popup__provider-link';

    bookingPopupEl.className = 'map-b-popup';
    bookingPopupEl.append(linkEl);

    return bookingPopupEl;
  }

  createSimpleMarkerElement(index: number, roundTrip: boolean): HTMLElement {
    const markerEl = document.createElement('div');
    const markerBox = document.createElement('div');
    const backEl = document.createElement('div');
    const textEl = document.createElement('p');

    textEl.innerText = String(index);
    textEl.className = 'map-marker__text';

    backEl.className = 'map-marker__background';

    markerBox.className = 'map-marker__box';
    markerBox.append(backEl, textEl);

    markerEl.className = 'map-marker';
    markerEl.style.setProperty('--m-pin-color', PIN_COLORS[index - 1]);
    markerEl.append(markerBox);

    if (roundTrip) {
      markerEl.classList.add('map-marker--round-trip');
    }

    return markerEl;
  }

  createStopoverMarkerElement(index: number): HTMLElement {
    const stopoverMarkerEl = document.createElement('div');
    const markerBox = document.createElement('div');
    const backEl = document.createElement('div');
    const textEl = document.createElement('p');

    textEl.innerText = String(PIN_ALPHABET[index]);
    textEl.className = 'map-marker__text';

    backEl.className =
      'map-marker__background map-marker__background--stopover';

    markerBox.className = 'map-marker__box';
    markerBox.append(backEl, textEl);

    stopoverMarkerEl.className = 'map-marker';
    stopoverMarkerEl.append(markerBox);

    return stopoverMarkerEl;
  }

  createGeodesicArc(coordinates: number[][]) {
    const [start, end] = coordinates;

    return {
      line: greatCircle(point(start), point(end), {
        npoints: 128,
      }) as any /**LineString**/,
      options: {
        id: `arc-line-feature-${this.generateUniqueId()}`,
        paint: {
          'line-color': '#747474',
          'line-width': 3,
          'line-opacity': 0.75,
          'line-offset': 2,
          'line-dasharray': [3, 3],
          'border-width': 0,
        },
      },
    };
  }

  generateUniqueId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
  }

  getDistance(start: number[], end: number[]): number {
    return distance(start, end, { units: 'kilometers' });
  }
}
